import React, { useState, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import * as Icons from 'lucide-react';

const ShippingLabelModal = ({
    isOpen,
    onClose,
    order,
    title = 'Imprimir Guías',
    subtitle = 'Configure cuántos rótulos desea generar para este envío.',
    onConfirm  // Opcional: si se proporciona, se ejecuta en lugar de print
}) => {
    const [boxCount, setBoxCount] = useState(1);
    const componentRef = useRef();

    const handlePrint = useReactToPrint({
        contentRef: componentRef,
        documentTitle: `Rotulos_${order?.order_number || 'Pedido'}`,
    });

    if (!isOpen || !order) return null;

    // Generate array of labels
    const labels = Array.from({ length: Math.max(1, boxCount) }, (_, i) => ({
        current: i + 1,
        total: Math.max(1, boxCount),
        order
    }));

    const customerName = order.customer_name || order.client_name || 'Cliente sin nombre';
    const address = order.shipping_address || order.address || 'Dirección no disponible';
    const city = order.city || order.customer_city || 'Ciudad no disponible';
    const phone = order.customer_phone || order.phone || '';
    const carrier = order.carrier_name || 'Transportadora no asignada';

    const handleGeneratePDF = () => {
        if (onConfirm) {
            // Si hay callback personalizado, usarlo (ej: abrir en nueva pestaña)
            onConfirm(boxCount);
        } else {
            // Si no, usar react-to-print (diálogo de impresión)
            handlePrint();
        }
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Background overlay with blur */}
            <div
                className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            ></div>

            {/* Modal Container */}
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden transform transition-all">
                <div className="p-6">
                    {/* Centered Icon */}
                    <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-blue-50 rounded-full">
                        <Icons.Printer className="w-8 h-8 text-blue-600" />
                    </div>

                    {/* Centered Title */}
                    <h3 className="text-xl font-bold text-center text-gray-900 mb-2">
                        {title}
                    </h3>
                    <p className="text-sm text-center text-gray-500 mb-6">
                        {subtitle}
                    </p>

                    {/* Box Count Input */}
                    <div className="space-y-4">
                        <div>
                            <label
                                htmlFor="box-count"
                                className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1 ml-1"
                            >
                                Cantidad de Cajas / Bultos
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="100"
                                id="box-count"
                                className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-100 rounded-xl focus:border-blue-500 focus:bg-white focus:outline-none transition-all text-lg font-bold text-center"
                                value={boxCount}
                                onChange={(e) => setBoxCount(parseInt(e.target.value) || 1)}
                                autoFocus
                                onFocus={(e) => e.target.select()}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleGeneratePDF();
                                }}
                            />
                        </div>

                        {/* Info Box */}
                        <div className="bg-blue-50/50 rounded-lg p-3 border border-blue-100">
                            <p className="text-xs text-blue-700 text-center leading-relaxed">
                                Se generará un documento con <strong>{boxCount}</strong> rótulos numerados (1 de {boxCount}, 2 de {boxCount}, etc.)
                            </p>
                        </div>
                    </div>
                </div>

                {/* Footer Buttons */}
                <div className="flex gap-3 p-6 bg-gray-50">
                    <button
                        type="button"
                        className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-200 text-sm font-bold text-gray-600 hover:bg-white hover:border-gray-300 transition-all"
                        onClick={onClose}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="flex-1 px-4 py-3 rounded-xl bg-blue-600 text-sm font-bold text-white hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all font-sans"
                        onClick={handleGeneratePDF}
                    >
                        Generar PDF
                    </button>
                </div>

                {/* Hidden Print Area */}
                <div style={{ display: 'none' }}>
                    <div ref={componentRef} className="print-container">
                        <style type="text/css" media="print">
                            {`
                @page { size: auto;  margin: 0mm; }
                .print-page { 
                  page-break-after: always; 
                  padding: 20px;
                  width: 100%;
                  height: 100vh;
                  display: flex;
                  flex-direction: column;
                  justify-content: center;
                  align-items: center;
                  font-family: Arial, sans-serif;
                  position: relative;
                }
                .watermark {
                  position: absolute;
                  top: 50%;
                  left: 50%;
                  transform: translate(-50%, -50%) rotate(-45deg);
                  font-size: 8rem;
                  font-weight: bold;
                  color: rgba(0, 0, 0, 0.05);
                  white-space: nowrap;
                  z-index: 0;
                  pointer-events: none;
                }
                .label-content {
                  border: 4px solid #000;
                  padding: 40px;
                  width: 90%;
                  max-width: 800px;
                  text-align: center;
                  position: relative;
                  z-index: 1;
                }
                .label-header {
                  font-size: 2.5rem;
                  font-weight: bold;
                  border-bottom: 2px solid #000;
                  margin-bottom: 20px;
                  padding-bottom: 10px;
                }
                .label-row {
                  font-size: 1.5rem;
                  margin: 15px 0;
                  text-align: left;
                }
                .label-row strong {
                  display: block;
                  font-size: 1rem;
                  color: #555;
                  text-transform: uppercase;
                }
                .label-box-counter {
                  font-size: 4rem;
                  font-weight: bold;
                  margin-top: 30px;
                  background: #000;
                  color: #fff;
                  padding: 10px 30px;
                  display: inline-block;
                  border-radius: 10px;
                }
                .label-footer {
                  margin-top: 40px;
                  font-size: 1rem;
                  color: #666;
                }
              `}
                        </style>
                        {labels.map((label, index) => (
                            <div key={index} className="print-page">
                                <div className="watermark">
                                    {label.current} / {label.total}
                                </div>
                                <div className="label-content">
                                    <div className="label-header">
                                        {carrier}
                                    </div>

                                    <div className="label-row">
                                        <strong>Destinatario:</strong>
                                        {customerName}
                                    </div>

                                    <div className="label-row">
                                        <strong>Dirección:</strong>
                                        {address}
                                    </div>

                                    <div className="label-row">
                                        <strong>Ciudad / Teléfono:</strong>
                                        {city} - {phone}
                                    </div>

                                    <div className="label-row">
                                        <strong>Pedido N°:</strong>
                                        {order.order_number}
                                    </div>

                                    {/* Only show "Cobrar Envío" if applicable */}
                                    {!order.shipping_fee_paid && order.delivery_method !== 'recoge_bodega' && (
                                        <div className="label-row" style={{ marginTop: '20px', border: '2px dashed red', padding: '10px', color: 'red', textAlign: 'center' }}>
                                            <strong style={{ color: 'red' }}>¡ATENCIÓN!</strong>
                                            COBRAR FLETE AL ENTREGAR
                                        </div>
                                    )}

                                    <div className="label-box-counter">
                                        {label.current} DE {label.total}
                                    </div>

                                    <div className="label-footer">
                                        ToppingFrozen - Logística
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShippingLabelModal;
